import { state, api, $, $$, toast, escHtml, truncate, _timeAgo, _ensureMachineCache, _allMachinesCache, _insertLineSelectBefore, _lineMap, formatDateTimeRO } from './core';
import { _initErrorsInDiag } from './parts';
import { askText, askConfirm } from './dialogs';
import type { ChatSource } from './types';
import type { RandDiagnosticCases, RandDocumentPageImages, RandErrorCodes, RandProcedureMedia } from './tipuri-baza';

/** un simptom din ghidul rapid: pașii de verificat când AI-ul nu răspunde */
interface Simptom { icon: string; steps: string[]; }
/** media unui pas, cum o trimite /api/extracts/<id>/steps */
type MediaPas = Pick<RandProcedureMedia, 'id' | 'media_type' | 'file_path' | 'url' | 'description' | 'uploaded_by'>;
/** un pas din /api/extracts/<id>/steps (routes/documents/extracte.py) — ruta redenumește coloanele tabelului
 *  (`instruction_ro` → `instruction`, `is_checkpoint` 0/1 → bool), deci nu e rândul din procedure_steps */
interface PasProcedura {
    id: number; step_number: number; instruction: string | null; duration_min: number | null;
    is_checkpoint: boolean; notes: string | null; media: MediaPas[];
}
/** răspunsul întreg de la /api/extracts/<id>/steps; `extract` are și el coloanele redenumite */
interface ProceduraRaspuns {
    extract: {
        id: number; type: string; title: string | null; content: string | null; tools: string | null;
        safety: string | null; references: string | null; video: string | null; machine_id: string;
        document_id: number | null; start_page: number | null; end_page: number | null; document_filename: string | null;
    };
    steps: PasProcedura[];
    general_media: MediaPas[];
}

/* ═══ AI DIAGNOSTIC ═══════════════════════════════════════════ */
export let diagInited = false;

export const SYMPTOM_DB: Record<string, Simptom> = {
    'Temperatură ridicată':     { icon: '🌡️', steps: ['Verificați nivelul lichidului de răcire', 'Inspectați filtrul de apă/ulei', 'Verificați funcționarea ventilatorului de răcire', 'Controlați termostat-ul și senzorii de temperatură', 'Verificați pompa de recirculare'] },
    'Presiune anormală':        { icon: '💨', steps: ['Verificați manometrul — calibrare ok?', 'Inspectați furtunurile hidraulice pentru scurgeri', 'Verificați supapele de siguranță', 'Controlați filtrul hidraulic (indicator colmatare)', 'Verificați pompa hidraulică — zgomote anormale?'] },
    'Vibrații excesive':        { icon: '📳', steps: ['Verificați strângerea șuruburilor de fixare', 'Inspectați rulmenții (zgomot, temperatură)', 'Controlați alinierea cuplajelor', 'Verificați echilibrarea dinamică', 'Inspectați amortizoarele de vibrații'] },
    'Zgomot neobișnuit':        { icon: '🔊', steps: ['Identificați sursa — motor, pompă, angrenaj?', 'Verificați nivelul lubrifiantului', 'Inspectați rulmenții și bucșele', 'Verificați jocul din angrenaje', 'Controlați curelele de transmisie'] },
    'Eroare pe display':        { icon: '⚠️', steps: ['Notați codul exact al erorii', 'Căutați codul în tab-ul Coduri eroare de mai sus', 'Verificați senzorii menționați în eroare', 'Resetați alarma și monitorizați recidiva', 'Documentați în jurnalul de mentenanță'] },
    'Piese defecte/uzate':      { icon: '🔧', steps: ['Identificați piesa — cod, localizare', 'Verificați stocul în modulul Piese', 'Fotografiați piesa pentru documentare', 'Demontați și inspectați vizual', 'Înlocuiți și testați funcționarea'] },
    'Scurgere ulei/lichid':     { icon: '💧', steps: ['Identificați tipul lichidului (ulei, apă, agent frig.)', 'Localizați sursa scurgerii', 'Verificați garnituirle și O-ring-urile', 'Inspectați racordurile și furtunurile', 'Strângeți/înlocuiți componenta defectă'] },
    'Produs non-conform':       { icon: '❌', steps: ['Verificați parametrii de proces (temp, presiune, timp)', 'Inspectați matriță — uzură, depuneri', 'Controlați materia primă — umiditate, contaminare', 'Verificați sistemul de răcire matriță', 'Ajustați parametrii și rulați test'] },
};

/** Comută tab-ul în secțiunea Diagnostic (ex. diag-tab-manual). */
export function activateDiagnosticTab(panelId: string): void {
    const tabsBar = document.getElementById('dx-tabs-bar');
    if (!tabsBar) return;
    const tabBtn = tabsBar.querySelector(`.dx-tab[data-tab="${panelId}"]`) as HTMLElement | null;
    tabsBar.querySelectorAll('.dx-tab').forEach(t => t.classList.remove('active'));
    document.querySelectorAll('#sec-diagnostic .dx-panel').forEach(c => c.classList.remove('active'));
    tabBtn?.classList.add('active');
    const panel = document.getElementById(panelId);
    panel?.classList.add('active');
    if (panelId === 'diag-tab-errors' && !window._errorsLoadedInDiag) {
        window._errorsLoadedInDiag = true;
        _initErrorsInDiag();
    }
    if (panelId === 'diag-tab-manual') {
        void loadDiagManualDocumentsList();
    }
    document.getElementById('sec-diagnostic')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

/**
 * După răspuns AI: deschide tab-ul „Caută în manual”, copiază linia/mașina, umple termenii și pornește căutarea.
 */
export async function openDiagManualFromAiResult(opts: {
    searchText: string;
    machineId: string;
    lineSelectId: string | null;
}): Promise<void> {
    const qInp = document.getElementById('diag-manual-q') as HTMLInputElement | null;
    const manualLine = document.getElementById('diag-manual-line') as HTMLSelectElement | null;
    const manualMach = document.getElementById('diag-manual-machine') as HTMLSelectElement | null;
    if (!qInp) {
        toast('Tab-ul manual nu e disponibil.', 'error');
        return;
    }

    let terms = (opts.searchText || '').trim().replace(/\s+/g, ' ');
    if (terms.length < 3 && opts.machineId) terms = `${opts.machineId} ${terms}`.trim();
    if (terms.length < 3) terms = 'reglaj procedură mentenanță';

    activateDiagnosticTab('diag-tab-manual');

    const srcLine = opts.lineSelectId ? document.getElementById(opts.lineSelectId) as HTMLSelectElement | null : null;
    if (manualLine && srcLine) {
        manualLine.value = srcLine.value;
        manualLine.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const setMachineAndSearch = () => {
        if (manualMach && opts.machineId) {
            const opt = Array.from(manualMach.options).find(o => o.value === opts.machineId);
            if (opt) manualMach.value = opts.machineId;
        }
        qInp.value = terms.slice(0, 500);
        void runDiagManualSearch();
    };

    if (manualLine && srcLine && srcLine.value) {
        window.setTimeout(() => {
            setMachineAndSearch();
            void loadDiagManualDocumentsList();
        }, 50);
    } else {
        setMachineAndSearch();
        void loadDiagManualDocumentsList();
    }
}

/** Etichetă afișată pentru doc_type + clasă CSS pentru badge. */
function _docTypePill(docType: string | undefined | null): { label: string; cls: string } {
    const raw = (docType || '').trim() || 'document';
    if (raw === 'document') {
        return { label: 'Necategorizat', cls: 'doc-type-pill--muted' };
    }
    const clsMap: Record<string, string> = {
        'Manual utilizare': 'doc-type-pill--manual',
        'Manual funcționare': 'doc-type-pill--manual',
        'Manual instalare': 'doc-type-pill--manual',
        'Carte tehnică': 'doc-type-pill--tech',
        'Fișă tehnică': 'doc-type-pill--tech',
        'Catalog piese': 'doc-type-pill--parts',
        'Diagrame': 'doc-type-pill--draw',
        'Desen tehnic': 'doc-type-pill--draw',
        'Scheme electrice': 'doc-type-pill--scheme',
        'Scheme mecanice': 'doc-type-pill--scheme',
        'Scheme service': 'doc-type-pill--scheme',
        'Declarație conformitate': 'doc-type-pill--legal',
    };
    return { label: raw, cls: clsMap[raw] || 'doc-type-pill--other' };
}

/** Încarcă lista PDF din DB pentru mașina selectată în tab-ul manual (fără căutare OCR). */
export async function loadDiagManualDocumentsList(): Promise<void> {
    const host = document.getElementById('diag-manual-docs-list');
    const mEl = document.getElementById('diag-manual-machine') as HTMLSelectElement | null;
    if (!host) return;
    const mid = (mEl?.value || '').trim();
    if (!mid) {
        host.innerHTML = '<div class="dx-empty">Selectați o <strong>mașină anume</strong> (dropdown) pentru a lista PDF-urile din baza de date. „Toate mașinile (fabrică)” rămâne pentru căutarea OCR în tot parcul.</div>';
        return;
    }
    host.innerHTML = '<div class="dx-empty">Se încarcă documentele…</div>';
    try {
        const res = await fetch(`/api/documents/for_machine/${encodeURIComponent(mid)}`);
        if (!res.ok) {
            const err = await res.json().catch(() => ({})) as { error?: string };
            host.innerHTML = `<div class="dx-empty" style="color:var(--danger)">${escHtml(err.error || 'Eroare la încărcare')}</div>`;
            return;
        }
        const data = await res.json() as { documents?: Array<{ id: number; filename?: string; doc_type?: string; language?: string; pages?: number | null; description?: string | null }> };
        const docs = data.documents || [];
        if (docs.length === 0) {
            host.innerHTML = `<div class="dx-empty">Nu există documente PDF asociate utilajului <strong>${escHtml(mid)}</strong>. Verificați mapările în modulul Documente.</div>`;
            return;
        }
        let html = `<div style="font-size:.78rem;color:var(--text-muted);margin-bottom:10px">${docs.length} PDF în baza de date pentru <strong>${escHtml(mid)}</strong> — sortate: manuale și carte tehnică primele.</div><ul class="diag-manual-doc-ul">`;
        docs.forEach((d) => {
            const fnRaw = d.filename || '—';
            const fn = escHtml(fnRaw);
            const descRaw = (d.description || '').trim();
            const hasDesc = descRaw.length > 0;
            const titleLine = hasDesc ? escHtml(descRaw) : fn;
            const fileLine = hasDesc ? `<span class="diag-manual-doc-filename">Fișier: ${fn}</span>` : '';
            const pill = _docTypePill(d.doc_type);
            const lang = d.language ? escHtml(String(d.language)) : '';
            const pages = d.pages != null ? `${d.pages} pag.` : '';
            const metaBits = [lang, pages].filter(Boolean);
            const meta = metaBits.length ? `<span class="diag-manual-doc-meta">${metaBits.join(' · ')}</span>` : '';
            const viewUrl = `/api/documents/${d.id}/view?page=1`;
            const fileUrl = `/api/documents/${d.id}/file`;
            html += `<li class="diag-manual-doc-li">
                <div class="diag-manual-doc-main">
                    <span class="doc-type-pill ${pill.cls}">${escHtml(pill.label)}</span>
                    <span class="diag-manual-doc-title">${titleLine}</span>
                    ${fileLine}
                    ${meta}
                </div>
                <div class="diag-manual-doc-actions">
                    <a href="${viewUrl}" target="_blank" rel="noopener" class="btn btn-sm dx-btn-primary" style="font-size:.78rem;text-decoration:none">📄 Deschide</a>
                    <a href="${fileUrl}" target="_blank" rel="noopener" class="btn btn-sm btn-outline" style="font-size:.78rem;text-decoration:none">⬇ PDF</a>
                </div>
            </li>`;
        });
        html += '</ul>';
        host.innerHTML = html;
    } catch (e) {
        host.innerHTML = `<div class="dx-empty" style="color:var(--danger)">${escHtml((e as Error).message || 'Eroare rețea')}</div>`;
    }
}

/** Pagină PDF pentru viewer (1-based), din page_number stocat (0-based) în document_raw_text / RAG. */
function _manualViewPage(r: { page?: number }): number {
    const p = r.page;
    return (typeof p === 'number' && !Number.isNaN(p) ? p : 0) + 1;
}

/** Căutare în manuale (Diagnostic): OCR + capitole relevante pe mașină. */
export async function runDiagManualSearch(): Promise<void> {
    const qEl = document.getElementById('diag-manual-q') as HTMLInputElement | null;
    const mEl = document.getElementById('diag-manual-machine') as HTMLSelectElement | null;
    const out = document.getElementById('diag-manual-results');
    const chOut = document.getElementById('diag-manual-chapters');
    if (!qEl || !out) return;

    const q = qEl.value.trim();
    if (q.length < 3) {
        toast('Introduceți cel puțin 3 caractere pentru căutare.', 'warning');
        qEl.focus();
        return;
    }

    const machineId = (mEl?.value || '').trim();
    const btn = document.getElementById('diag-manual-search') as HTMLButtonElement | null;
    if (btn) { btn.disabled = true; btn.textContent = '⏳ Se caută...'; }
    out.innerHTML = '<div class="dx-empty">Se caută în documente…</div>';
    if (chOut) {
        chOut.style.display = 'none';
        chOut.innerHTML = '';
    }

    try {
        const params = new URLSearchParams({ q, limit: '25' });
        if (machineId) params.set('machine_id', machineId);

        const [rawRes, chRes] = await Promise.all([
            fetch(`/api/raw_text/search?${params.toString()}`),
            machineId
                ? fetch(`/api/documents/relevant_chapters?${new URLSearchParams({ machine_id: machineId, problem: q }).toString()}`)
                : Promise.resolve(null as Response | null),
        ]);

        if (!rawRes.ok) {
            const err = await rawRes.json().catch(() => ({}));
            out.innerHTML = `<div class="dx-empty" style="color:var(--danger)">${escHtml((err as { error?: string }).error || 'Eroare la căutare')}</div>`;
            return;
        }

        const data = await rawRes.json() as { results?: Array<{ document_id: number; page?: number; filename?: string; machine_id?: string; snippet?: string; relevance?: number }> };
        const rows = data.results || [];

        if (chOut && chRes && chRes.ok) {
            const chapters = await chRes.json() as Array<{ title?: string; chapter_number?: string; filename?: string; pdf_url?: string; relevance?: number }>;
            if (chapters && chapters.length > 0) {
                chOut.style.display = 'block';
                let chHtml = '<h4 style="font-size:.85rem;margin:0 0 8px;color:var(--cyan)">📑 Capitole din cuprins (potrivire titlu)</h4><ul style="margin:0;padding-left:18px;font-size:.88rem;line-height:1.5">';
                chapters.slice(0, 12).forEach((c) => {
                    const href = c.pdf_url || '#';
                    const title = escHtml((c.chapter_number ? c.chapter_number + ' ' : '') + (c.title || 'Capitol'));
                    const fn = c.filename ? ` <span style="color:var(--text-muted)">(${escHtml(c.filename)})</span>` : '';
                    chHtml += `<li style="margin-bottom:6px"><a href="${escHtml(href)}" target="_blank" rel="noopener">${title}</a>${fn}</li>`;
                });
                chHtml += '</ul>';
                chOut.innerHTML = chHtml;
            }
        }

        if (rows.length === 0) {
            out.innerHTML = '<div class="dx-empty">Niciun fragment găsit. Încercați alți termeni sau o altă mașină.</div>';
            return;
        }

        let html = `<div style="font-size:.8rem;color:var(--text-muted);margin-bottom:10px">${rows.length} rezultat(e) în textul OCR al manualelor${machineId ? ` — filtru: <strong>${escHtml(machineId)}</strong>` : ''}</div>`;
        rows.forEach((r) => {
            const viewPg = _manualViewPage(r);
            const href = `/api/documents/${r.document_id}/view?page=${viewPg}`;
            const fn = escHtml(r.filename || 'document');
            const mid = r.machine_id ? escHtml(r.machine_id) : '';
            const snip = escHtml((r.snippet || '').replace(/\s+/g, ' ').trim());
            const rel = typeof r.relevance === 'number' ? `<span style="font-size:.75rem;color:var(--text-muted)"> scor: ${r.relevance.toFixed(3)}</span>` : '';
            html += `<div class="manual-search-hit" style="border:1px solid var(--border);border-radius:8px;padding:10px 12px;margin-bottom:10px;background:var(--surface)">
                <div style="display:flex;flex-wrap:wrap;gap:8px;align-items:center;margin-bottom:6px">
                    <a href="${href}" target="_blank" rel="noopener" class="dx-btn-primary" style="font-size:.8rem;padding:4px 10px;text-decoration:none">📄 Deschide PDF pag. ${viewPg}</a>
                    <span style="font-size:.8rem;color:var(--cyan)">${fn}</span>${mid ? `<span style="font-size:.78rem;color:var(--text-muted)">${mid}</span>` : ''}${rel}
                </div>
                <div style="font-size:.85rem;line-height:1.45;color:var(--text);white-space:pre-wrap;word-break:break-word">${snip}</div>
            </div>`;
        });
        out.innerHTML = html;
    } catch (e) {
        out.innerHTML = `<div class="dx-empty" style="color:var(--danger)">${escHtml((e as Error).message || 'Eroare rețea')}</div>`;
    } finally {
        if (btn) { btn.disabled = false; btn.textContent = '🔍 Caută în manuale'; }
    }
}

export async function initDiagnostic() {
    if (diagInited) return;
    diagInited = true;

    const tabsBar = document.getElementById('dx-tabs-bar');
    if (tabsBar) {
        tabsBar.querySelectorAll('.dx-tab').forEach(tab => {
            tab.addEventListener('click', () => {
                tabsBar.querySelectorAll('.dx-tab').forEach(t => t.classList.remove('active'));
                document.querySelectorAll('#sec-diagnostic .dx-panel').forEach(c => c.classList.remove('active'));
                tab.classList.add('active');
                const target = document.getElementById((tab as HTMLElement).dataset.tab!);
                if (target) target.classList.add('active');
                if ((tab as HTMLElement).dataset.tab === 'diag-tab-errors' && !window._errorsLoadedInDiag) {
                    window._errorsLoadedInDiag = true;
                    _initErrorsInDiag();
                }
                if ((tab as HTMLElement).dataset.tab === 'diag-tab-manual') {
                    void loadDiagManualDocumentsList();
                }
            });
        });
    }

    await _ensureMachineCache();
    const machines = _allMachinesCache || [];

    const mSel = $('#diag-machine');
    mSel!.innerHTML = '<option value="">Selectează mașina...</option>';
    machines.forEach(m => { mSel!.innerHTML += `<option value="${m.id}">${m.id} — ${escHtml(m.name || '')}</option>`; });
    _insertLineSelectBefore(mSel, 'diag-line', 'Toate liniile', 'Selectează mașina...');

    const descMSel = $('#diag-desc-machine');
    if (descMSel) {
        descMSel.innerHTML = '<option value="">Selectează mașina...</option>';
        machines.forEach(m => { descMSel.innerHTML += `<option value="${m.id}">${m.id} — ${escHtml(m.name || '')}</option>`; });
        _insertLineSelectBefore(descMSel, 'diag-desc-line', 'Toate liniile', 'Selectează mașina...');
    }

    const manualMSel = document.getElementById('diag-manual-machine') as HTMLSelectElement | null;
    if (manualMSel) {
        manualMSel.innerHTML = '<option value="">Toate mașinile (fabrică)</option>';
        machines.forEach(m => { manualMSel.innerHTML += `<option value="${m.id}">${m.id} — ${escHtml(m.name || '')}</option>`; });
        _insertLineSelectBefore(manualMSel, 'diag-manual-line', 'Toate liniile', 'Toate mașinile');
    }
    document.getElementById('diag-manual-search')?.addEventListener('click', () => { void runDiagManualSearch(); });
    document.getElementById('diag-manual-from-describe')?.addEventListener('click', () => {
        const t = ($('#diag-desc-text') as HTMLTextAreaElement | null)?.value?.trim() || '';
        const inp = document.getElementById('diag-manual-q') as HTMLInputElement | null;
        if (inp) inp.value = t.slice(0, 500);
        toast(t ? 'Text copiat în căutarea din manual.' : 'Nu există text în „Descrie problema”.', t ? 'info' : 'warning');
    });
    document.getElementById('diag-manual-q')?.addEventListener('keydown', (ev) => {
        if ((ev as KeyboardEvent).key === 'Enter') {
            ev.preventDefault();
            void runDiagManualSearch();
        }
    });
    document.getElementById('diag-manual-machine')?.addEventListener('change', () => { void loadDiagManualDocumentsList(); });
    document.getElementById('diag-manual-line')?.addEventListener('change', () => {
        window.setTimeout(() => void loadDiagManualDocumentsList(), 80);
    });

    document.getElementById('diag-desc-start')?.addEventListener('click', async () => {
        const machine = ($('#diag-desc-machine') as HTMLInputElement)?.value || '';
        const text = ($('#diag-desc-text') as HTMLInputElement)?.value?.trim() || '';
        if (!text || text.length < 5) { toast('Descrie problema mai detaliat!', 'warning'); $('#diag-desc-text')?.focus(); return; }
        const btn = document.getElementById('diag-desc-start');
        (btn as HTMLInputElement).disabled = true; btn!.textContent = '⏳ Se analizează...';
        try {
            const query = (machine ? 'Mașina: ' + machine + '. ' : '') +
                'Problema/eroarea raportată: ' + text +
                '\n\nRăspunde cu un diagnostic structurat:' +
                '\n1. Ce ar putea cauza problema (1-3 cauze probabile)' +
                '\n2. Pași de verificare (numerotați, concreți)' +
                '\n3. Soluție recomandată' +
                '\nFolosește limbaj simplu. Formatează cu markdown.';
            const res = await fetch('/api/chat', { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ message: query, machine_id: machine || undefined, context_module: 'diagnostics' }) });
            const data = await res.json();
            const answer = data.response || data.reply || 'Nu s-a putut genera diagnosticul.';
            const srcTag = data.source === 'local'
                ? `<span class="chat-source-tag local">🟢 AI Local (${Math.round((data.confidence||0)*100)}%)</span>`
                : data.source === 'cloud' ? '<span class="chat-source-tag cloud">🔵 AI Cloud</span>' : '';
            let html = srcTag + '<div style="white-space:pre-wrap;line-height:1.6;font-size:.95rem">' +
                renderMarkdown(answer) + '</div>';
            if (data.sources && data.sources.length > 0) {
                const diImgs = data.sources.filter((s: ChatSource) => s.image_url);
                if (diImgs.length) {
                    html += '<div style="display:flex;gap:8px;flex-wrap:wrap;margin-top:8px">';
                    diImgs.forEach((s: ChatSource) => {
                        html += `<img src="${escHtml(s.image_url)}" alt="${escHtml(s.title||'')}" style="max-width:180px;max-height:130px;border-radius:6px;cursor:pointer;border:1px solid var(--border)" onclick="_openLightbox('${escHtml(s.image_url)}')">`;
                    });
                    html += '</div>';
                }
                html += '<div class="chat-sources diag-sources" style="margin-top:10px"><span class="chat-sources-label">📎 Surse:</span> ';
                data.sources.forEach((s: ChatSource) => {
                    const icon = s.type === 'image_analysis' ? '🖼️' : s.type === 'procedure' ? '📋' : s.type === 'interval' ? '🔧' : s.type === 'part' ? '⚙️' : s.type === 'raw_page' ? '📄' : '📄';
                    const pgAttr = s.page_number != null ? ` data-page="${s.page_number + 1}"` : '';
                    html += `<button class="chat-source-btn" data-type="${s.source || ''}" data-id="${s.source_id || ''}"${pgAttr} title="${escHtml(s.title || '')}">${icon} ${escHtml((s.title||'').substring(0,50))}</button> `;
                });
                html += '</div>';
            }
            html += `<button type="button" class="diag-to-manual-btn" id="diag-to-manual-from-desc">📄 Caută asta în manual (PDF)</button>`;
            if (data.source === 'local') {
                html += `<button class="chat-escalate-btn" id="diag-desc-escalate">🔵 Solicită detalii suplimentare (AI Cloud)</button>`;
            }
            html += _createFeedbackHtml(query, answer, data.source || 'unknown', data.sources || []);
            document.getElementById('diag-desc-results')!.innerHTML = html;
            _attachSourceClickHandlers(document.getElementById('diag-desc-results'));
            document.getElementById('diag-to-manual-from-desc')?.addEventListener('click', () => {
                void openDiagManualFromAiResult({ searchText: text, machineId: machine, lineSelectId: 'diag-desc-line' });
            });
            document.getElementById('diag-desc-escalate')?.addEventListener('click', async function() {
                (this as HTMLInputElement).disabled = true; this.textContent = 'Se procesează...';
                try {
                    const r2 = await fetch('/api/chat', { method:'POST', headers:{'Content-Type':'application/json'},
                        body: JSON.stringify({ message: query, machine_id: machine || undefined, escalate: true }) });
                    const d2 = await r2.json();
                    if (d2.response) {
                        const escalateHtml = '<hr style="border-color:var(--border);margin:12px 0">' +
                            '<span class="chat-source-tag cloud">🔵 AI Cloud</span>' +
                            '<div style="white-space:pre-wrap;line-height:1.6;font-size:.95rem;margin-top:8px">' +
                            renderMarkdown(d2.response) + '</div>' +
                            _createFeedbackHtml(query + ' (escalare cloud)', d2.response, 'cloud', d2.sources || []);
                        const escalDiv = Object.assign(document.createElement('div'), { innerHTML: escalateHtml });
                        this.replaceWith(escalDiv);
                        _attachSourceClickHandlers(escalDiv);
                    }
                } catch(e2) { toast('Eroare escalare', 'error'); }
            });
            const saveWrap = document.getElementById('diag-save-case-wrap');
            if (saveWrap) { saveWrap.classList.remove('hidden'); saveWrap.dataset.machineId = machine; saveWrap.dataset.symptom = text.substring(0, 100); }
        } catch(e) { toast('Eroare: ' + (e as Error).message, 'error'); }
        (btn as HTMLInputElement).disabled = false; btn!.textContent = '🔍 Diagnostichează cu AI';
    });

    const sSel = $('#diag-symptom');
    sSel!.innerHTML = '<option value="">Selectează simptomul...</option>';
    Object.entries(SYMPTOM_DB).forEach(([name, data]) => {
        sSel!.innerHTML += `<option value="${name}">${data.icon} ${name}</option>`;
    });

    $('#diag-start')!.addEventListener('click', runDiagnostic);

    document.getElementById('diag-save-case-btn')?.addEventListener('click', async () => {
        const wrap = document.getElementById('diag-save-case-wrap');
        const machineId = (wrap && wrap.dataset.machineId) || ($('#diag-machine') as HTMLInputElement).value || '';
        const symptom = (wrap && wrap.dataset.symptom) || ($('#diag-symptom') as HTMLInputElement).value || '';
        const cause = ($('#diag-cause') as HTMLInputElement).value.trim();
        const actions = ($('#diag-actions') as HTMLInputElement).value.trim();
        const outcome = ($('#diag-outcome') as HTMLInputElement).value.trim();
        const addedBy = ($('#diag-operator') as HTMLInputElement).value.trim();
        try {
            const res = await fetch('/api/diagnostic_cases', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    machine_id: machineId || null,
                    symptom: symptom || 'Simptom necunoscut',
                    cause_found: cause || null,
                    actions_taken: actions || null,
                    outcome: outcome || null,
                    added_by: addedBy || null
                })
            });
            const data = await res.json();
            if (res.ok) {
                toast('Caz salvat. Necesită validare inginer pentru a deveni lecție.');
                loadDiagnosticLessons();
            } else {
                toast(data.error || 'Eroare la salvare', 'error');
            }
        } catch (e) {
            toast('Eroare: ' + ((e as Error).message || 'necunoscută'), 'error');
        }
    });

    /* batch AI button removed — processing is automatic on validation */

    document.getElementById('lessons-refresh-btn')?.addEventListener('click', () => loadDiagnosticLessons());

    let _lessonsMode: string | undefined = 'validated';
    document.querySelectorAll('[data-lessons-tab]').forEach(btn => {
        btn.addEventListener('click', () => {
            _lessonsMode = (btn as HTMLElement).dataset.lessonsTab;
            document.querySelectorAll('[data-lessons-tab]').forEach(b => b.classList.remove('btn-active'));
            btn.classList.add('btn-active');
            loadDiagnosticLessons(_lessonsMode);
        });
    });
    window._getLessonsMode = () => _lessonsMode;

    const isEngineer = state.currentUser && (state.currentUser.role === 'master' || state.currentUser.role === 'engineer');
    if (isEngineer) {
        const pendingTab = document.getElementById('lessons-tab-pending');
        if (pendingTab) pendingTab.style.display = '';
        _loadPendingLessonsCount();
    }

    const descLineEl = document.getElementById('diag-desc-line');
    const descMachEl = document.getElementById('diag-desc-machine');
    const diagLineEl = document.getElementById('diag-line');
    const diagMachEl = document.getElementById('diag-machine');
    [descLineEl, descMachEl, diagLineEl, diagMachEl].forEach(el => {
        if (el) el.addEventListener('change', () => { loadDiagnosticLessons(window._getLessonsMode()); _loadFilteredFrequentErrors(); });
    });

    await loadDiagnosticLessons();

    await _loadFilteredFrequentErrors();
}

export async function _loadFilteredFrequentErrors() {
    const kbList = document.getElementById('diag-kb-list');
    if (!kbList) return;

    const params = new URLSearchParams({ per_page: '15' });
    const descLine = document.getElementById('diag-desc-line');
    const descMachine = document.getElementById('diag-desc-machine');
    const diagLine = document.getElementById('diag-line');
    const diagMachine = document.getElementById('diag-machine');
    const activeMachine = (descMachine as HTMLInputElement)?.value || (diagMachine as HTMLInputElement)?.value || '';
    const activeLine = (descLine as HTMLInputElement)?.value || (diagLine as HTMLInputElement)?.value || '';

    if (activeMachine) {
        params.set('machine_id', activeMachine);
    } else if (activeLine && _lineMap && _lineMap[activeLine]) {
        const ids = _lineMap[activeLine].map(m => m.id).join(',');
        params.set('machine_ids', ids);
    }

    try {
        const errorsTop = await api('/api/error_codes?' + params);
        kbList.innerHTML = '';
        (errorsTop.data || []).forEach((e: RandErrorCodes) => {
            const div = document.createElement('div');
            div.className = 'diag-kb-item';
            div.innerHTML = `
                <div class="diag-kb-code">${escHtml(e.machine_id)} — ${escHtml(e.error_code)}</div>
                <div class="diag-kb-desc">${escHtml(truncate(e.description_ro, 100))}</div>
            `;
            div.addEventListener('click', () => {
                const res = document.getElementById('diag-results');
                if (!res) return;
                // rezultatul stă în fila „Simptom predefinit" — dacă era ascunsă, clicul părea mort
                activateDiagnosticTab('diag-tab-symptom');
                setTimeout(() => res.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
                res.innerHTML = `
                    <div class="diag-step" style="border-left-color:var(--orange)">
                        <span class="diag-step-num">📋</span>
                        <div class="diag-step-text">
                            <strong>${escHtml(e.error_code)}</strong> — ${escHtml(e.description_ro)}<br>
                            ${e.probable_cause ? `<br><strong>Cauză:</strong> ${escHtml(e.probable_cause)}` : ''}
                            ${e.solution ? `<br><strong>Soluție:</strong> ${escHtml(e.solution)}` : ''}
                            ${e.parts_needed ? `<br><strong>Piese necesare:</strong> ${escHtml(e.parts_needed)}` : ''}
                        </div>
                    </div>`;
            });
            kbList.appendChild(div);
        });
        if ((errorsTop.data || []).length === 0) {
            kbList.innerHTML = '<div class="alarm-empty">Nicio eroare frecventă pentru selecția curentă.</div>';
        }
    } catch (e) {
        kbList.innerHTML = '<div class="alarm-empty">Eroare la încărcare</div>';
    }
}

export async function runDiagnostic() {
    const machineId = ($('#diag-machine') as HTMLInputElement).value;
    const symptom = ($('#diag-symptom') as HTMLInputElement).value;
    if (!symptom) { toast('Selectați un simptom', 'error'); return; }

    const sympData = SYMPTOM_DB[symptom];
    const res = $('#diag-results');
    const btn = $('#diag-start');
    (btn as HTMLInputElement).disabled = true; btn!.textContent = '⏳ Se caută în documentație...';

    let html = `<h4 style="font-size:.8rem;margin-bottom:.5rem;color:var(--orange)">
        ${sympData.icon} Diagnostic: ${symptom}${machineId ? ` — ${machineId}` : ''}</h4>`;

    try {
        const query = `Diagnostic pentru simptom: ${symptom}` + (machineId ? ` la mașina ${machineId}` : '') +
            `. Dă pași concreți de verificare și rezolvare bazați pe documentația tehnică.`;
        const aiRes = await fetch('/api/chat', {
            method: 'POST', headers: {'Content-Type':'application/json'},
            body: JSON.stringify({ message: query, machine_id: machineId, context_module: 'diagnostics' })
        });
        const aiData = await aiRes.json();

        if (aiData.response && !aiData.error) {
            const srcTag = aiData.source === 'local'
                ? `<span class="chat-source-tag local">🟢 AI Local (${Math.round((aiData.confidence||0)*100)}%)</span>`
                : aiData.source === 'cloud' ? '<span class="chat-source-tag cloud">🔵 AI Cloud</span>' : '';
            html += srcTag;
            html += '<div style="white-space:pre-wrap;line-height:1.6;font-size:.93rem;margin-top:8px">' +
                renderMarkdown(aiData.response) + '</div>';

            if (aiData.sources && aiData.sources.length > 0) {
                const dsImgs = aiData.sources.filter((s: ChatSource) => s.image_url);
                if (dsImgs.length) {
                    html += '<div style="display:flex;gap:8px;flex-wrap:wrap;margin-top:8px">';
                    dsImgs.forEach((s: ChatSource) => {
                        html += `<img src="${escHtml(s.image_url)}" alt="${escHtml(s.title||'')}" style="max-width:180px;max-height:130px;border-radius:6px;cursor:pointer;border:1px solid var(--border)" onclick="_openLightbox('${escHtml(s.image_url)}')">`;
                    });
                    html += '</div>';
                }
                html += '<div class="chat-sources diag-sources" style="margin-top:12px"><span class="chat-sources-label">📎 Surse din documentație:</span> ';
                aiData.sources.forEach((s: ChatSource) => {
                    const icon = s.type === 'image_analysis' ? '🖼️' : s.type === 'procedure' ? '📋' : s.type === 'interval' ? '🔧' : s.type === 'part' ? '⚙️' : s.type === 'raw_page' ? '📄' : '📄';
                    const pgAttr = s.page_number != null ? ` data-page="${s.page_number + 1}"` : '';
                    html += `<button class="chat-source-btn" data-type="${s.source || ''}" data-id="${s.source_id || ''}"${pgAttr} title="${escHtml(s.title || '')}">${icon} ${escHtml((s.title||'').substring(0,50))}</button> `;
                });
                html += '</div>';
            }

            html += `<button type="button" class="diag-to-manual-btn" id="diag-to-manual-from-symptom">📄 Caută asta în manual (PDF)</button>`;
            if (aiData.source === 'local') {
                html += `<button class="chat-escalate-btn" onclick="this.disabled=true;this.textContent='Se procesează...';_diagEscalate('${escHtml(symptom)}','${escHtml(machineId)}')">
                    🔵 Solicită detalii suplimentare (AI Cloud)</button>`;
            }
            html += _createFeedbackHtml(query, aiData.response, aiData.source || 'unknown', aiData.sources || []);
        } else {
            sympData.steps.forEach((step: string, i: number) => {
                html += `<div class="diag-step"><span class="diag-step-num">${i + 1}</span><div class="diag-step-text">${step}</div></div>`;
            });
        }
    } catch(e) {
        sympData.steps.forEach((step: string, i: number) => {
            html += `<div class="diag-step"><span class="diag-step-num">${i + 1}</span><div class="diag-step-text">${step}</div></div>`;
        });
    }

    (btn as HTMLInputElement).disabled = false; btn!.textContent = '🔍 Pornește diagnostic';

    if (machineId) {
        const errors = await api(`/api/error_codes?machine_id=${machineId}&per_page=10`);
        if (errors.data.length > 0) {
            html += `<h4 style="font-size:.72rem;margin:.8rem 0 .4rem;color:var(--text-muted);letter-spacing:1px">CODURI EROARE RELEVANTE PENTRU ${machineId}</h4>`;
            errors.data.slice(0, 5).forEach((e: RandErrorCodes) => {
                html += `<div class="diag-step" style="border-left-color:var(--red)">
                    <span class="diag-step-num">⚠</span>
                    <div class="diag-step-text">
                        <strong>${escHtml(e.error_code)}</strong>: ${escHtml(truncate(e.description_ro, 120))}
                        ${e.solution ? `<br><em style="color:var(--text-muted)">${escHtml(truncate(e.solution, 100))}</em>` : ''}
                    </div>
                </div>`;
            });
        }

        const intervals = await api(`/api/maintenance_intervals?machine_id=${machineId}`);
        if (intervals.length > 0) {
            html += `<h4 style="font-size:.72rem;margin:.8rem 0 .4rem;color:var(--text-muted);letter-spacing:1px">INTERVALE MENTENANȚĂ ${machineId}</h4>`;
            intervals.slice(0, 5).forEach((iv: { action_ro: string|null|undefined; interval_text: string|null|undefined; }) => {
                html += `<div class="diag-step" style="border-left-color:var(--green)">
                    <span class="diag-step-num">🔧</span>
                    <div class="diag-step-text">${escHtml(iv.action_ro)} — <strong>${escHtml(iv.interval_text)}</strong></div>
                </div>`;
            });
        }
    }

    res!.innerHTML = html;
    _attachSourceClickHandlers(res);
    document.getElementById('diag-to-manual-from-symptom')?.addEventListener('click', () => {
        const st = (machineId ? `${symptom} ${machineId}` : symptom).trim();
        void openDiagManualFromAiResult({ searchText: st, machineId: machineId || '', lineSelectId: 'diag-line' });
    });

    const saveWrap = document.getElementById('diag-save-case-wrap');
    if (saveWrap) {
        saveWrap.classList.remove('hidden');
        saveWrap.dataset.machineId = machineId || '';
        saveWrap.dataset.symptom = symptom || '';
        ($('#diag-cause') as HTMLInputElement).value = '';
        ($('#diag-actions') as HTMLInputElement).value = '';
        ($('#diag-outcome') as HTMLInputElement).value = '';
        const op = (state.currentUser && state.currentUser.username) || '';
        ($('#diag-operator') as HTMLInputElement).value = op;
    }
}

export async function _diagEscalate(symptom: string, machineId: string) {
    const query = `Diagnostic detaliat pentru simptom: ${symptom}` + (machineId ? ` la mașina ${machineId}` : '') +
        `. Dă pași concreți de verificare și rezolvare bazați pe documentația tehnică. Am nevoie de detalii suplimentare.`;
    try {
        const aiRes = await fetch('/api/chat', {
            method: 'POST', headers: {'Content-Type':'application/json'},
            body: JSON.stringify({ message: query, machine_id: machineId, escalate: true, context_module: 'diagnostics' })
        });
        const aiData = await aiRes.json();
        if (aiData.response && !aiData.error) {
            const res = $('#diag-results');
            const existing = res!.innerHTML;
            res!.innerHTML = existing.replace(/<button class="chat-escalate-btn"[^>]*>[\s\S]*?<\/button>/, '') +
                '<hr style="border-color:var(--border);margin:12px 0">' +
                '<span class="chat-source-tag cloud">🔵 AI Cloud - Detalii suplimentare</span>' +
                '<div style="white-space:pre-wrap;line-height:1.6;font-size:.93rem;margin-top:8px">' +
                renderMarkdown(aiData.response) + '</div>' +
                _createFeedbackHtml(query, aiData.response, 'cloud', aiData.sources || []);
            _attachSourceClickHandlers(res);
        }
    } catch(e) { toast('Eroare escalare: ' + (e as Error).message, 'error'); }
}

export async function loadDiagnosticLessons(mode?: string) {
    const listEl = document.getElementById('diag-lessons-list');
    if (!listEl) return;
    const showValidated = (mode || 'validated') === 'validated';
    try {
        const params = new URLSearchParams({ limit: '30' });
        params.set('validated', showValidated ? '1' : '0');

        const descLine = document.getElementById('diag-desc-line');
        const descMachine = document.getElementById('diag-desc-machine');
        const diagLine = document.getElementById('diag-line');
        const diagMachine = document.getElementById('diag-machine');

        const activeLine = (descLine as HTMLInputElement)?.value || (diagLine as HTMLInputElement)?.value || '';
        const activeMachine = (descMachine as HTMLInputElement)?.value || (diagMachine as HTMLInputElement)?.value || '';

        if (activeMachine) {
            params.set('machine_id', activeMachine);
        } else if (activeLine && _lineMap && _lineMap[activeLine]) {
            params.set('machine_ids', _lineMap[activeLine].map(m => m.id).join(','));
        }

        const fromDate = (document.getElementById('lessons-from') as HTMLInputElement)?.value;
        const toDate = (document.getElementById('lessons-to') as HTMLInputElement)?.value;
        if (fromDate) params.set('from_date', fromDate);
        if (toDate) params.set('to_date', toDate);

        const r = await api('/api/diagnostic_cases?' + params);
        const data = r.data || [];
        listEl.innerHTML = '';
        // inginerul vede fila „Așteaptă validare" și serverul îl lasă să valideze/corecteze —
        // doar butoanele lipseau (30 sep 2026: fila era un capăt de drum pentru ingineri)
        const isAdmin = state.currentUser && (state.currentUser.role === 'master' || state.currentUser.role === 'engineer');

        data.forEach((c: RandDiagnosticCases) => {
            const div = document.createElement('div');
            div.className = 'diag-lesson-item';
            const srcLabel = c.source_type === 'intervention' ? '🔧' : c.source_type === 'alarm' ? '🚨' : '🔍';
            const dateStr = (c.created_at || '').slice(0, 16);
            const meta = [srcLabel, c.machine_id || '—', dateStr].filter(Boolean).join(' · ');
            let html = `<div class="meta">${escHtml(meta)}</div>`;
            html += `<div style="font-weight:600;margin:4px 0">${escHtml(c.symptom || '')}</div>`;
            if (c.cause_found) html += `<div style="font-size:.9rem;color:var(--text-muted)"><strong>Cauză:</strong> ${escHtml(c.cause_found)}</div>`;
            if (c.actions_taken) html += `<div style="font-size:.9rem;color:var(--text-muted)"><strong>Acțiuni:</strong> ${escHtml(truncate(c.actions_taken, 150))}</div>`;
            if (c.ai_processed && c.ai_suggested_steps) {
                html += `<div class="ai-steps">${escHtml(truncate(c.ai_suggested_steps, 400))}</div>`;
            } else {
                html += `<div style="font-size:.8rem;color:var(--text-muted);font-style:italic">⏳ AI procesează sugestiile...</div>`;
            }
            if (c.validated_by) {
                html += `<div style="font-size:.75rem;color:var(--text-muted);margin-top:4px">Validat de ${escHtml(c.validated_by)}${c.validated_at ? ' la ' + escHtml(formatDateTimeRO(c.validated_at)) : ''}</div>`;
            }
            if (isAdmin && showValidated) {
                html += `<button type="button" class="btn btn-sm lesson-edit-btn" data-case-id="${c.id}" style="margin-top:4px;font-size:.8rem">✏️ Corectează</button>`;
            }
            if (isAdmin && !showValidated && !c.validated) {
                html += `<button type="button" class="btn btn-sm btn-green lesson-validate-btn" data-case-id="${c.id}" style="margin-top:4px;font-size:.8rem">✅ Validează</button>`;
            }
            div.innerHTML = html;
            listEl.appendChild(div);
        });

        if (data.length === 0) {
            const filterNote = (activeMachine || activeLine) ? ' pentru filtrul selectat' : '';
            const emptyMsg = showValidated
                ? `Nicio lecție validată${filterNote}. Lecțiile se creează automat la validarea intervențiilor.`
                : `Niciun caz în așteptare${filterNote}.`;
            listEl.innerHTML = `<div class="alarm-empty"><span class="icon">📚</span>${emptyMsg}</div>`;
        }

        listEl.querySelectorAll('.lesson-edit-btn').forEach(btn => {
            btn.addEventListener('click', () => _openLessonEditor(parseInt((btn as HTMLElement).dataset.caseId!)));
        });
        listEl.querySelectorAll('.lesson-validate-btn').forEach(btn => {
            btn.addEventListener('click', async () => {
                const id = parseInt((btn as HTMLElement).dataset.caseId!);
                (btn as HTMLInputElement).disabled = true;
                btn.textContent = 'Se validează…';
                try {
                    await api('/api/diagnostic_cases/' + id, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            validate: true,
                            validated_by: state.currentUser?.username || 'Inginer'
                        })
                    });
                    toast('Caz validat! AI procesează sugestii automat.');
                    loadDiagnosticLessons(window._getLessonsMode());
                    _loadPendingLessonsCount();
                } catch (e) { toast('Eroare: ' + (e as Error).message, 'error'); (btn as HTMLInputElement).disabled = false; btn.textContent = '✅ Validează'; }
            });
        });
    } catch (e) {
        listEl.innerHTML = '<div class="alarm-empty">Eroare la încărcare</div>';
    }
}

export async function _loadPendingLessonsCount() {
    try {
        const r = await api('/api/diagnostic_cases?validated=0&limit=200');
        const count = (r.data || []).filter((c: RandDiagnosticCases) => !c.validated).length;
        const el = document.getElementById('lessons-pending-count');
        if (el) el.textContent = count > 0 ? count : '';
    } catch (e) { /* ignore */ }
}

export async function _openLessonEditor(caseId: string|number) {
    const r = await api('/api/diagnostic_cases?limit=200').catch(() => ({ data: [] }));
    const c = (r.data || []).find((x: RandDiagnosticCases) => x.id === caseId);
    if (!c) { toast('Lecția nu a fost găsită', 'error'); return; }
    let existing = document.getElementById('lesson-edit-overlay');
    if (existing) existing.remove();

    const overlay = document.createElement('div');
    overlay.id = 'lesson-edit-overlay';
    overlay.className = 'proc-overlay';
    overlay.innerHTML = `
      <div class="proc-container" style="max-width:750px;overflow-y:auto">
        <div class="proc-header"><div class="proc-header-top">
          <h2 class="proc-title">✏️ Corectare lecție #${caseId}</h2>
          <button class="proc-close" id="lesson-edit-close">&times;</button>
        </div></div>
        <div class="proc-body" style="padding:16px">
          <div style="margin-bottom:10px">
            <label style="font-weight:600;font-size:.9rem">Simptom / problemă:</label>
            <textarea id="le-symptom" rows="2" style="width:100%;padding:8px;border-radius:6px;border:1px solid var(--border);background:var(--bg-main);color:var(--text-main);margin-top:4px">${escHtml(c.symptom || '')}</textarea>
          </div>
          <div style="margin-bottom:10px">
            <label style="font-weight:600;font-size:.9rem">Cauză identificată:</label>
            <textarea id="le-cause" rows="2" style="width:100%;padding:8px;border-radius:6px;border:1px solid var(--border);background:var(--bg-main);color:var(--text-main);margin-top:4px">${escHtml(c.cause_found || '')}</textarea>
          </div>
          <div style="margin-bottom:10px">
            <label style="font-weight:600;font-size:.9rem">Acțiuni efectuate:</label>
            <textarea id="le-actions" rows="3" style="width:100%;padding:8px;border-radius:6px;border:1px solid var(--border);background:var(--bg-main);color:var(--text-main);margin-top:4px">${escHtml(c.actions_taken || '')}</textarea>
          </div>
          <div style="margin-bottom:10px">
            <label style="font-weight:600;font-size:.9rem">Sugestii AI (pot fi corectate):</label>
            <textarea id="le-ai-steps" rows="14" style="width:100%;min-height:200px;padding:8px;border-radius:6px;border:1px solid var(--border);background:var(--bg-main);color:var(--text-main);margin-top:4px;resize:vertical">${escHtml(c.ai_suggested_steps || '')}</textarea>
          </div>
          <button class="btn btn-green" id="le-save" style="width:100%;padding:10px">💾 Salvează corectările</button>
        </div>
      </div>`;
    document.body.appendChild(overlay);
    document.getElementById('lesson-edit-close')!.addEventListener('click', () => overlay.remove());

    document.getElementById('le-save')!.addEventListener('click', async () => {
        try {
            await api('/api/diagnostic_cases/' + caseId, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    symptom: (document.getElementById('le-symptom') as HTMLInputElement).value.trim(),
                    cause_found: (document.getElementById('le-cause') as HTMLInputElement).value.trim(),
                    actions_taken: (document.getElementById('le-actions') as HTMLInputElement).value.trim(),
                    ai_suggested_steps: (document.getElementById('le-ai-steps') as HTMLInputElement).value.trim(),
                })
            });
            toast('Lecția a fost corectată.');
            overlay.remove();
            loadDiagnosticLessons();
        } catch (e) { toast('Eroare: ' + (e as Error).message, 'error'); }
    });
}

/* ═══ PROCEDURE OVERLAY ═══════════════════════════════════════ */

export let _procTimer: ReturnType<typeof setInterval> | null = null;
export let _procStartTime: number | null = null;
export let _procCompletionId = null;

export function _fmtTimer(ms: number) {
    const s = Math.floor(ms / 1000);
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return (h ? h + ':' : '') + String(m).padStart(2, '0') + ':' + String(sec).padStart(2, '0');
}

export async function openProcedureOverlay(extractId: number | string) {
    try {
        const resp = await fetch(`/api/extracts/${extractId}/steps`);
        if (!resp.ok) throw new Error('Eroare la încărcare');
        const data: ProceduraRaspuns = await resp.json();
        const ext = data.extract;
        const steps = data.steps || [];
        const generalMedia = data.general_media || [];

        if (steps.length === 0) {
            toast('Procedura nu are pași definiți încă.', 'warning');
            return;
        }

        let existing = document.getElementById('proc-overlay');
        if (existing) existing.remove();

        const overlay = document.createElement('div');
        overlay.id = 'proc-overlay';
        overlay.className = 'proc-overlay';

        let stepsHtml = '';
        steps.forEach((s: PasProcedura) => {
            const mediaHtml = (s.media || []).map((m) => {
                if (m.media_type === 'image' && m.file_path) return `<img src="/uploads/${escHtml(m.file_path)}" alt="${escHtml(m.description || '')}" class="proc-step-img">`;
                if (m.url) return `<a href="${escHtml(m.url)}" target="_blank" class="proc-step-link">▶ ${escHtml(m.description || 'Video')}</a>`;
                return '';
            }).join('');
            const ckClass = s.is_checkpoint ? ' proc-step-checkpoint' : '';
            const ckIcon = s.is_checkpoint ? '<span class="proc-ck-icon" title="Punct de siguranță">⚠</span>' : '';
            const notesHtml = s.notes ? `<div class="proc-step-notes"><em>${escHtml(s.notes)}</em></div>` : '';
            const stepPageHint = (ext.start_page != null && ext.end_page != null)
                ? `<span class="proc-step-page-hint" title="Din paginile ${ext.start_page+1}–${ext.end_page+1} ale documentului">p.${ext.start_page+1}–${ext.end_page+1}</span>`
                : '';
            stepsHtml += `
                <div class="proc-step${ckClass}" data-step="${s.step_number}" data-extract-id="${extractId}">
                    <label class="proc-step-label">
                        <input type="checkbox" class="proc-step-cb" data-step="${s.step_number}">
                        <span class="proc-step-num">${s.step_number}</span>
                        <span class="proc-step-text">${ckIcon}${escHtml(s.instruction)}</span>
                        ${stepPageHint}
                    </label>
                    <div class="proc-step-actions">
                        <button class="proc-ctx-btn" data-step="${s.step_number}" data-extract-id="${extractId}" title="Sugestii și interpretare AI">🔍 Sugestii AI</button>
                        <button class="proc-img-btn" data-step="${s.step_number}" data-extract-id="${extractId}" title="Imagini din manual">🖼 Imagini</button>
                        <button class="proc-openpdf-btn" data-step="${s.step_number}" data-extract-id="${extractId}" title="Deschide PDF-ul original">📄 Deschide PDF</button>
                    </div>
                    <div class="proc-pdf-context hidden" id="pdf-ctx-${s.step_number}"></div>
                    <div class="proc-step-images hidden" id="step-imgs-${s.step_number}"></div>
                    ${mediaHtml}
                    ${notesHtml}
                </div>`;
        });

        const toolsHtml = ext.tools ? `<div class="proc-meta-row"><span class="proc-meta-label">SCULE</span><span class="proc-meta-val">${escHtml(ext.tools)}</span></div>` : '';
        const safetyHtml = ext.safety ? `<div class="proc-meta-row proc-meta-safety"><span class="proc-meta-label">⚠ SIGURANȚĂ</span><span class="proc-meta-val">${escHtml(ext.safety)}</span></div>` : '';
        const refsHtml = ext.references ? `<div class="proc-meta-row"><span class="proc-meta-label">REFERINȚĂ</span><span class="proc-meta-val">${escHtml(ext.references)}</span></div>` : '';

        let pageInfoHtml = '';
        if (ext.start_page != null && ext.end_page != null && ext.document_id) {
            const pageLabel = ext.start_page === ext.end_page
                ? `Pagina ${ext.start_page + 1}`
                : `Paginile ${ext.start_page + 1} – ${ext.end_page + 1}`;
            const docName = ext.document_filename ? ` din ${escHtml(ext.document_filename)}` : '';
            pageInfoHtml = `<div class="proc-meta-row proc-meta-source">
                <span class="proc-meta-label">📄 SURSĂ</span>
                <span class="proc-meta-val">
                    <a href="/api/documents/${ext.document_id}/view?page=${ext.start_page + 1}" target="_blank" class="proc-pdf-source-link">${pageLabel}${docName}</a>
                </span>
            </div>`;
        } else if (ext.document_id) {
            pageInfoHtml = `<div class="proc-meta-row proc-meta-source" id="proc-source-dynamic">
                <span class="proc-meta-label">📄 SURSĂ</span>
                <span class="proc-meta-val"><span class="loading-dots">Se caută pagina relevantă...</span></span>
            </div>`;
        }

        overlay.innerHTML = `
            <div class="proc-container">
                <div class="proc-header">
                    <div class="proc-header-top">
                        <h2 class="proc-title">${escHtml(ext.title || 'Procedură')}</h2>
                        <button class="proc-close" title="Închide">&times;</button>
                    </div>
                    <div class="proc-type-badge">${escHtml(ext.type || 'procedure')}</div>
                    ${toolsHtml}${safetyHtml}${refsHtml}${pageInfoHtml}
                </div>
                <div class="proc-steps-list">
                    ${stepsHtml}
                </div>
                <div class="proc-footer">
                    <div class="proc-progress">
                        <div class="proc-progress-bar"><div class="proc-progress-fill" style="width:0%"></div></div>
                        <span class="proc-progress-text">0 / ${steps.length} pași</span>
                    </div>
                    <div class="proc-timer">00:00</div>
                    <div class="proc-actions">
                        <button class="proc-btn proc-btn-note" title="Adaugă notă">📝 Notă</button>
                        <button class="proc-btn proc-btn-finish" disabled>✓ Finalizează</button>
                    </div>
                </div>
            </div>`;

        document.body.appendChild(overlay);
        document.body.style.overflow = 'hidden';

        if (!ext.start_page && ext.document_id && steps.length > 0) {
            fetch(`/api/extracts/${extractId}/steps/1/pdf_context`)
                .then(r => r.ok ? r.json() : null)
                .then(d => {
                    const el = document.getElementById('proc-source-dynamic');
                    if (!el || !d || !d.results || !d.results.length) return;
                    const best = d.results[0];
                    const docName = best.filename ? ` din ${escHtml(best.filename)}` : '';
                    el.querySelector('.proc-meta-val')!.innerHTML =
                        `<a href="/api/documents/${best.document_id}/view?page=${best.page}" target="_blank" class="proc-pdf-source-link">Pagina ${best.page}${docName}</a>`;
                }).catch(() => {});
        }

        _procStartTime = Date.now();
        const timerEl = overlay.querySelector('.proc-timer');
        _procTimer = setInterval(() => {
            timerEl!.textContent = _fmtTimer(Date.now() - _procStartTime!);
        }, 1000);

        // Start completion tracking
        try {
            const cResp = await fetch(`/api/extracts/${extractId}/completions`, {
                method: 'POST', headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({guidance_mode: 'tablet'})
            });
            if (cResp.ok) {
                const cData = await cResp.json();
                _procCompletionId = cData.completion_id;
            }
        } catch(e) { /* silent */ }

        const totalSteps = steps.length;
        const progressFill = overlay.querySelector('.proc-progress-fill');
        const progressText = overlay.querySelector('.proc-progress-text');
        const finishBtn = overlay.querySelector('.proc-btn-finish');

        function updateProgress() {
            const checked = overlay.querySelectorAll('.proc-step-cb:checked').length;
            const pct = Math.round((checked / totalSteps) * 100);
            (progressFill as HTMLElement).style.width = pct + '%';
            progressText!.textContent = `${checked} / ${totalSteps} pași`;
            (finishBtn as HTMLInputElement).disabled = checked < totalSteps;

            if (_procCompletionId) {
                fetch(`/api/completions/${_procCompletionId}`, {
                    method: 'PUT', headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify({steps_completed: checked})
                }).catch(() => {});
            }
        }

        overlay.querySelectorAll('.proc-step-cb').forEach(cb => {
            cb.addEventListener('change', () => {
                const stepEl = cb.closest('.proc-step');
                stepEl!.classList.toggle('proc-step-done', (cb as HTMLInputElement).checked);
                stepEl!.querySelectorAll('.proc-ctx-btn, .proc-img-btn, .proc-openpdf-btn').forEach(b => { (b as HTMLInputElement).disabled = (cb as HTMLInputElement).checked; });
                if ((cb as HTMLInputElement).checked) {
                    const ctxDiv = stepEl!.querySelector('.proc-pdf-context');
                    if (ctxDiv) { (ctxDiv as HTMLElement).style.display = 'none'; }
                    const ctxBtn = stepEl!.querySelector('.proc-ctx-btn');
                    if (ctxBtn) ctxBtn.textContent = '🔍 Sugestii AI';
                    const stepNum = parseInt((cb as HTMLElement).dataset.step!);
                    const nextStep = overlay.querySelector(`.proc-step[data-step="${stepNum + 1}"]`);
                    if (nextStep) nextStep.scrollIntoView({behavior: 'smooth', block: 'center'});
                }
                updateProgress();
            });
        });

        overlay.querySelectorAll('.proc-ctx-btn').forEach(btn => {
            btn.addEventListener('click', (e) => {
                e.stopPropagation();
                const stepNum = (btn as HTMLElement).dataset.step;
                const ctxDiv = document.getElementById('pdf-ctx-' + stepNum);
                if (!ctxDiv) return;

                if (ctxDiv.style.display !== 'none' && ctxDiv.dataset.loaded) {
                    ctxDiv.style.display = 'none';
                    btn.textContent = '🔍 Sugestii AI';
                    return;
                }

                if (!ctxDiv.dataset.loaded) {
                    let html = '<div class="pdf-ctx-header">💡 Interpretare AI din documentație:</div>';
                    if (ext.tools) html += `<div class="ai-ctx-row"><span class="ai-ctx-label">SCULE</span><span class="ai-ctx-val">${escHtml(ext.tools)}</span></div>`;
                    if (ext.safety) html += `<div class="ai-ctx-row ai-ctx-safety"><span class="ai-ctx-label">⚠ SIGURANȚĂ</span><span class="ai-ctx-val">${escHtml(ext.safety)}</span></div>`;
                    if (ext.references) html += `<div class="ai-ctx-row"><span class="ai-ctx-label">REFERINȚĂ</span><span class="ai-ctx-val">${escHtml(ext.references)}</span></div>`;
                    if (ext.content) {
                        const lines = ext.content.split('\n').filter((l: string) => l.trim());
                        const stepIdx = parseInt(stepNum!) - 1;
                        const contextStart = Math.max(0, stepIdx - 1);
                        const contextEnd = Math.min(lines.length, stepIdx + 3);
                        const contextLines = lines.slice(contextStart, contextEnd);
                        if (contextLines.length > 0) {
                            html += '<div class="ai-ctx-detail"><div class="ai-ctx-detail-title">Context complet:</div>';
                            contextLines.forEach((line: string|null|undefined) => {
                                const isCurrent = line!.trim().replace(/^\d+\.\s*/, '') === steps[stepIdx]?.instruction;
                                html += `<div class="ai-ctx-line${isCurrent ? ' ai-ctx-current' : ''}">${escHtml(line)}</div>`;
                            });
                            html += '</div>';
                        }
                    }
                    if (ext.video) html += `<div class="ai-ctx-row"><span class="ai-ctx-label">VIDEO</span><span class="ai-ctx-val">${escHtml(ext.video)}</span></div>`;
                    ctxDiv.innerHTML = html;
                    ctxDiv.dataset.loaded = '1';
                }
                ctxDiv.style.display = 'block';
                ctxDiv.classList.remove('hidden');
                btn.textContent = '🔍 Ascunde sugestii';
            });
        });

        overlay.querySelectorAll('.proc-openpdf-btn').forEach(btn => {
            btn.addEventListener('click', async (e) => {
                e.stopPropagation();
                const stepNum = (btn as HTMLElement).dataset.step;
                const extId = (btn as HTMLElement).dataset.extractId;
                btn.textContent = '⏳ Se caută...';
                try {
                    const resp = await fetch(`/api/extracts/${extId}/steps/${stepNum}/pdf_context`);
                    const data = await resp.json();
                    if (data.results && data.results.length > 0) {
                        const best = data.results[0];
                        window.open(`/api/documents/${best.document_id}/view?page=${best.page}`, '_blank');
                    } else if (data.document_id) {
                        window.open(`/api/documents/${data.document_id}/file`, '_blank');
                    } else {
                        toast('Nu s-a găsit documentul PDF', 'warning');
                    }
                } catch(err) {
                    toast('Eroare la deschidere PDF', 'error');
                }
                btn.textContent = '📄 Deschide PDF original';
            });
        });

        overlay.querySelectorAll('.proc-img-btn').forEach(btn => {
            btn.addEventListener('click', async (e) => {
                e.stopPropagation();
                const stepNum = (btn as HTMLElement).dataset.step;
                const extId = (btn as HTMLElement).dataset.extractId;
                const imgDiv = document.getElementById('step-imgs-' + stepNum);
                if (!imgDiv) return;
                if (imgDiv.style.display !== 'none' && imgDiv.dataset.loaded) {
                    imgDiv.style.display = 'none';
                    btn.textContent = '🖼 Imagini';
                    return;
                }
                if (!imgDiv.dataset.loaded) {
                    btn.textContent = '⏳ Se încarcă...';
                    try {
                        const resp = await fetch(`/api/extracts/${extId}/steps/${stepNum}/images`);
                        const imgs = await resp.json();
                        if (imgs.length === 0) {
                            imgDiv.innerHTML = '<p class="proc-no-images">Nu s-au găsit imagini pentru acest pas. Apăsați "Extrage imagini" la document pentru a genera.</p>';
                        } else {
                            imgDiv.innerHTML = imgs.map((img: RandDocumentPageImages) =>
                                `<div class="proc-img-wrap"><img src="/uploads/${escHtml(img.file_path)}" alt="Pagina ${img.page_number + 1}" class="proc-step-diagram" loading="lazy"><span class="proc-img-caption">Doc #${img.document_id} - Pagina ${img.page_number + 1}</span></div>`
                            ).join('');
                        }
                        imgDiv.dataset.loaded = '1';
                    } catch(err) {
                        imgDiv.innerHTML = '<p class="proc-no-images">Eroare la încărcarea imaginilor</p>';
                    }
                }
                imgDiv.style.display = 'block';
                imgDiv.classList.remove('hidden');
                btn.textContent = '🖼 Ascunde imagini';
            });
        });

        overlay.querySelector('.proc-close')!.addEventListener('click', () => closeProcOverlay(overlay));

        overlay.querySelector('.proc-btn-note')!.addEventListener('click', async () => {
            const note = await askText('Notă procedură', { label: 'Adaugă o notă pentru această procedură:', multiline: true });
            if (note && _procCompletionId) {
                fetch(`/api/completions/${_procCompletionId}`, {
                    method: 'PUT', headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify({notes: note})
                }).catch(() => {});
                toast('Notă salvată', 'success');
            }
        });

        finishBtn!.addEventListener('click', async () => {
            if (!await askConfirm('Finalizezi procedura? Toți pașii sunt confirmați.')) return;
            if (_procCompletionId) {
                await fetch(`/api/completions/${_procCompletionId}`, {
                    method: 'PUT', headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify({finalize: true, steps_completed: totalSteps})
                }).catch(() => {});
            }
            toast('Procedură finalizată cu succes!', 'success');
            closeProcOverlay(overlay);
        });

    } catch (e) {
        toast('Eroare: ' + ((e as Error).message || 'necunoscută'), 'error');
    }
}

export function closeProcOverlay(overlay: HTMLDivElement) {
    if (_procTimer) { clearInterval(_procTimer); _procTimer = null; }
    document.body.style.overflow = '';
    overlay.remove();
}
